/**
 * Navigation Configuration
 *
 * Centralized navigation structure for the POS application.
 * Menu items are role-based with hierarchical inheritance:
 *   cashier < manager < admin
 * Each role inherits menu items from all roles below it.
 */

import { SvgIconProps } from "@mui/material";
import React from "react";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

/**
 * User role types for permission-based navigation
 */
export type UserRole =
  | "superadmin"
  | "admin"
  | "manager"
  | "cashier"
  | "webhook";

/**
 * Role hierarchy levels (higher number = more permissions)
 */
export const roleHierarchy: Record<UserRole, number> = {
  webhook: 0,
  cashier: 1,
  manager: 2,
  admin: 3,
  superadmin: 4,
};

/**
 * Check if a user role has access to a given required role level
 */
export function hasRoleAccess(
  userRole: UserRole,
  requiredRole: UserRole,
): boolean {
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Derive the primary (highest) role from a roles array
 */
export function getPrimaryRole(roles: string[] | undefined): UserRole {
  if (!roles || roles.length === 0) return "cashier";
  if (roles.includes("superadmin")) return "superadmin";
  if (roles.includes("admin")) return "admin";
  if (roles.includes("manager")) return "manager";
  if (roles.includes("webhook")) return "webhook";
  return "cashier";
}

/**
 * Check if user has any of the specified roles
 */
export function userHasAnyRole(
  userRoles: string[] | undefined,
  requiredRoles: UserRole | UserRole[],
): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return roles.some((role) => userRoles.includes(role));
}

/**
 * Navigation menu item configuration
 */
export interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  requiredRole?: UserRole;
  children?: NavItem[];
}

/**
 * All navigation items, ordered for display.
 * `requiredRole` = minimum role needed to see the item.
 *
 * Cashier  : Caisse, Mes clients, Mes documents
 * Manager  : (cashier items) + Accueil, Articles, Rapport, Utilisateurs, Statistiques
 * Admin    : (manager items) + Paramètres
 */
export const navItems: NavItem[] = [
  // ── Superadmin (dedicated) ──
  {
    path: "/superadmin/entities",
    label: "Gestion entité",
    icon: BusinessIcon,
    requiredRole: "superadmin",
  },
  {
    path: "/superadmin/administration",
    label: "Administration",
    icon: AdminPanelSettingsIcon,
    requiredRole: "superadmin",
  },

  // ── Manager+ ──
  {
    path: "/home",
    label: "Accueil",
    icon: DashboardIcon,
    requiredRole: "manager",
  },

  // ── Cashier+ ──
  {
    path: "/pos",
    label: "Caisse",
    icon: PointOfSaleIcon,
    requiredRole: "cashier",
  },
  {
    path: "/admin/customers",
    label: "Mes clients",
    icon: PeopleIcon,
    requiredRole: "cashier",
  },
  {
    path: "/exports-documents",
    label: "Exports & Documents",
    icon: DescriptionIcon,
    requiredRole: "cashier",
  },

  // ── Manager+ ──
  {
    path: "/admin/items",
    label: "Articles",
    icon: InventoryIcon,
    requiredRole: "manager",
    children: [
      {
        path: "/admin/categories",
        label: "Catégories",
        icon: CategoryIcon,
        requiredRole: "manager",
      },
    ],
  },
  {
    path: "/admin/users",
    label: "Utilisateurs",
    icon: PersonIcon,
    requiredRole: "manager",
  },
  {
    path: "/stats",
    label: "Statistiques",
    icon: BarChartIcon,
    requiredRole: "manager",
  },

  // ── Admin+ ──
  {
    path: "/admin/settings",
    label: "Paramètres",
    icon: SettingsIcon,
    requiredRole: "admin",
  },
];

/**
 * Navigation items grouped by role tier.
 * A user sees their own tier + all tiers below.
 */
export interface RoleNavSection {
  role: UserRole;
  label: string;
  items: NavItem[];
}

export const navSections: RoleNavSection[] = [
  {
    role: "cashier",
    label: "Caisse",
    items: [
      { path: "/pos", label: "Caisse", icon: PointOfSaleIcon },
      { path: "/admin/customers", label: "Mes clients", icon: PeopleIcon },
      {
        path: "/exports-documents",
        label: "Exports & Documents",
        icon: DescriptionIcon,
      },
    ],
  },
  {
    role: "manager",
    label: "Manager",
    items: [
      { path: "/home", label: "Accueil", icon: DashboardIcon },
      { path: "/admin/items", label: "Articles", icon: InventoryIcon },
      { path: "/admin/users", label: "Utilisateurs", icon: PersonIcon },
      { path: "/stats", label: "Statistiques", icon: BarChartIcon },
    ],
  },
  {
    role: "admin",
    label: "Admin",
    items: [
      { path: "/home", label: "Accueil", icon: DashboardIcon },
      { path: "/admin/settings", label: "Paramètres", icon: SettingsIcon },
    ],
  },
];

/**
 * Get the filtered list of nav items for a given role
 */
export function getNavItemsForRole(role: UserRole): NavItem[] {
  if (role === "superadmin") {
    return navItems.filter((item) => item.requiredRole === "superadmin");
  }
  return navItems.filter((item) => hasRoleAccess(role, item.requiredRole!));
}

/**
 * Get visible nav sections for a given role (includes all lower tiers).
 */
export function getNavSectionsForRole(role: UserRole): RoleNavSection[] {
  const level = roleHierarchy[role] || 0;
  return navSections.filter((s) => roleHierarchy[s.role] <= level);
}
